import {
  validateAppointmentStepOneBookingDraft,
  type AppointmentBookingDraftV1,
} from "@/app/booking/core/appointmentBookingDraft";
import {
  validatePackagePurchaseDraft,
  type PackagePurchaseDraftV1,
} from "@/app/commerce/packages/packagePurchaseDraft";

import {
  readSpecialistLocationGatewayLineageByDestinationStrict,
} from "./authorityStore";
import type { SpecialistLocationGatewayCommittedNavigationLineageV1 } from "./contract";
import {
  createSpecialistLocationGatewayAppointmentDestinationFingerprint,
  createSpecialistLocationGatewayAppointmentDestinationFingerprintFromStepOneFingerprint,
  createSpecialistLocationGatewayPackageDestinationFingerprint,
} from "./destinationTransactions";

export type SpecialistLocationGatewayDestinationLineagePairResult<Draft> =
  | {
      status: "ready";
      draft: Draft;
      lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
    }
  | {
      status: "unavailable";
      reason:
        | "invalid-destination"
        | "lineage-absent"
        | "lineage-owner-mismatch"
        | "lineage-unreadable"
        | "not-specialist-origin";
    };

export function readSpecialistLocationGatewayAppointmentLineageByEvidence(input: {
  destinationDraftId: string;
  sourceAppointmentDraftFingerprint: string;
  entryPoint: AppointmentBookingDraftV1["entryPoint"];
  expectedCustomerAccountId: string | null;
}):
  | {
      status: "ready";
      lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
    }
  | {
      status: "unavailable";
      reason:
        | "lineage-absent"
        | "lineage-owner-mismatch"
        | "lineage-unreadable"
        | "not-specialist-origin";
    } {
  if (input.entryPoint.kind !== "specialist-services") {
    return { status: "unavailable", reason: "not-specialist-origin" };
  }
  const destinationDraftFingerprint =
    createSpecialistLocationGatewayAppointmentDestinationFingerprintFromStepOneFingerprint(
      input.sourceAppointmentDraftFingerprint,
    );
  if (!destinationDraftFingerprint) {
    return { status: "unavailable", reason: "lineage-owner-mismatch" };
  }
  const lineage = readSpecialistLocationGatewayLineageByDestinationStrict({
    destinationDraftId: input.destinationDraftId,
    expectedDestinationFingerprint: destinationDraftFingerprint,
    expectedCustomerAccountId: input.expectedCustomerAccountId,
    expectedDestinationKind: "individual",
    expectedSelectedLocationId: input.entryPoint.locationId,
    expectedSelectedUserLocationId: input.entryPoint.userLocationId,
  });
  return lineage.status === "present"
    ? { status: "ready", lineage: lineage.lineage }
    : {
        status: "unavailable",
        reason:
          lineage.status === "absent"
            ? "lineage-absent"
            : lineage.status === "owner-mismatch"
              ? "lineage-owner-mismatch"
              : "lineage-unreadable",
      };
}

export function readSpecialistLocationGatewayAppointmentDestinationPair(input: {
  draft: unknown;
  expectedCustomerAccountId: string | null;
}): SpecialistLocationGatewayDestinationLineagePairResult<AppointmentBookingDraftV1> {
  const validation = validateAppointmentStepOneBookingDraft(input.draft);
  if (!validation.valid) {
    return { status: "unavailable", reason: "invalid-destination" };
  }
  const draft = validation.draft;
  if (draft.entryPoint.kind !== "specialist-services") {
    return { status: "unavailable", reason: "not-specialist-origin" };
  }
  const fingerprint =
    createSpecialistLocationGatewayAppointmentDestinationFingerprint(draft);
  if (!fingerprint) {
    return { status: "unavailable", reason: "invalid-destination" };
  }
  const lineage = readSpecialistLocationGatewayLineageByDestinationStrict({
    destinationDraftId: draft.draftId,
    expectedDestinationFingerprint: fingerprint,
    expectedCustomerAccountId: input.expectedCustomerAccountId,
    expectedDestinationKind: "individual",
    expectedSelectedLocationId: draft.locationId,
    expectedSelectedUserLocationId: draft.entryPoint.userLocationId,
  });
  return lineage.status === "present"
    ? { status: "ready", draft, lineage: lineage.lineage }
    : {
        status: "unavailable",
        reason:
          lineage.status === "absent"
            ? "lineage-absent"
            : lineage.status === "owner-mismatch"
              ? "lineage-owner-mismatch"
              : "lineage-unreadable",
      };
}

export function readSpecialistLocationGatewayPackageDestinationPair(input: {
  draft: unknown;
  expectedCustomerAccountId: string | null;
}): SpecialistLocationGatewayDestinationLineagePairResult<PackagePurchaseDraftV1> {
  const validation = validatePackagePurchaseDraft(input.draft);
  if (!validation.valid) {
    return { status: "unavailable", reason: "invalid-destination" };
  }
  const draft = validation.draft;
  if (draft.entryPoint.kind !== "specialist-packages") {
    return { status: "unavailable", reason: "not-specialist-origin" };
  }
  const fingerprint =
    createSpecialistLocationGatewayPackageDestinationFingerprint(draft);
  if (!fingerprint) {
    return { status: "unavailable", reason: "invalid-destination" };
  }
  const lineage = readSpecialistLocationGatewayLineageByDestinationStrict({
    destinationDraftId: draft.draftId,
    expectedDestinationFingerprint: fingerprint,
    expectedCustomerAccountId: input.expectedCustomerAccountId,
    expectedDestinationKind: "package",
    expectedSelectedLocationId: draft.locationId,
    expectedSelectedUserLocationId: draft.userLocationId,
  });
  return lineage.status === "present"
    ? { status: "ready", draft, lineage: lineage.lineage }
    : {
        status: "unavailable",
        reason:
          lineage.status === "absent"
            ? "lineage-absent"
            : lineage.status === "owner-mismatch"
              ? "lineage-owner-mismatch"
              : "lineage-unreadable",
      };
}
